import { useEffect, useState } from 'react'
import { getEnv } from '@unni/platform'

import { DoctorService } from '@/services/doctors/doctor.service'
import StructuredData from '@/components/StructuredData'
import { HospitalDetailTemplate } from '@/features/hospital/components/hospital-detail-template'
import ResponsiveLayout from '@/components/atomic/templates/ResponsiveLayout'
import TopDownloadScreen from '@/components/atomic/templates/TopDownloadScreen'
import { postAmplitude, postGA } from '@/core/lib'
import { HospitalService } from '@/features/hospital/api/hospital.service'
import { getHospitalReviewsInternal } from '@/features/review/api/get-hospital-reivews'
import { ServiceOffer } from '@/features/service-offer/service-offer.type'
import { ServiceOfferService } from '@/features/service-offer/api/service-offer.service'
import { useLocales } from '@/shared/hooks/useLocales'
import { createSolarApiClient } from '@/shared/libs/api-client'
import ContextEnhancer, { ApiHeaders } from '@/shared/libs/context-enhancer'
import { getDefaultMeta, SEOMeta } from '@/shared/utils'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { getServerTranslation } from '@/utils/i18n/i18n-server'
import { Countries, CountryCodes, Locales } from '@/utils/language'
import { createHospitalSchema, createDoctorSchema, createReviewSchema, createOrganizationSchema } from '@/utils/schema'
import { User } from '@/features/member/member.type'
import { HospitalView } from '@/features/hospital/types/hospital.type'

import type { DoctorForUser } from '@/services/doctors/doctor.types'
import type { ReviewsPaginationResponseInternal } from '@/features/review/api/review.type'
import type { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next'

export interface HospitalDetailPageProps {
  user: User | null
  data: {
    hospital: HospitalView
    doctors: DoctorForUser[]
    events: ServiceOffer[]
    reviewWithPagination?: ReviewsPaginationResponseInternal
  }
  meta: SEOMeta
}

const HospitalDetailPage: NextPage<HospitalDetailPageProps> = ({
  user,
  data: { hospital, doctors, events, reviewWithPagination },
}) => {
  const { locale } = useLocales()
  const [commonAmplitudeProperties, setCommonAmplitudeProperties] = useState({})

  useEffect(() => {
    postAmplitude('view_hospital', {
      hospital_id: hospital.hospitalId,
      hospital_country: hospital.country,
      hospital_region: hospital.location.sido,
    })
  }, [hospital])

  useEffect(() => {
    setCommonAmplitudeProperties({
      hospital_id: hospital.hospitalId,
      hospital_name: hospital.name,
      hospital_region: hospital.location.sido,
      path: `${location.pathname}${location.search}`,
      referrer: document.referrer,
    })
  }, [hospital])

  useEffect(() => {
    postGA('view_hospital', 'pageview', `HP_${hospital.hospitalId}`)
  }, [hospital.hospitalId])

  useEffect(() => {
    if (user && user.id !== 0) {
      sessionStorage.setItem('user_id', user.id.toString())
    } else {
      sessionStorage.removeItem('user_id')
    }
  }, [user])

  return (
    <ResponsiveLayout>
      <TopDownloadScreen userProfileImage={user?.profileImage} bottomPaddingToAdd="108px">
        <HospitalDetailTemplate
          user={user}
          models={{
            hospital,
            doctors,
            events,
            reviewWithPagination,
          }}
          optionalMarketingItems={[]}
          onClickConsultation={() => {
            sendAirbridgeEvent(AirbridgeEvents.CONVERSION_BUTTON_CLICK, {
              semanticAttributes: {
                type: 'HOSPITAL',
                products: [
                  {
                    productID: '',
                    name: '병원페이지 상담신청',
                    categoryID: '',
                    categoryName: '',
                    brandID: hospital.hospitalId.toString(),
                    brandName: hospital.name,
                    price: 0,
                    currency: hospital.country === CountryCodes.KR ? 'KRW' : 'JPY',
                  },
                ],
              },
            })
          }}
          onClickTab={(target) => {
            postAmplitude('click_tab_in_hospital_detail', {
              ...commonAmplitudeProperties,
              target,
            })
          }}
          onClickMoreList={(target) =>
            postAmplitude(`click_more_${target}_info_in_hospital_detail`, {
              ...commonAmplitudeProperties,
              count_item: target === 'hospital' ? hospital.badges.length : doctors.length,
            })
          }
          onClickCopyAddress={() =>
            postAmplitude('click_copy_address_in_hospital_detail', {
              ...commonAmplitudeProperties,
            })
          }
          onClickMap={() =>
            postAmplitude('click_map_in_hospital_detail', {
              ...commonAmplitudeProperties,
            })
          }
        >
          <HospitalDetailTemplate.PhotoSlider />
          <HospitalDetailTemplate.Header />
          <HospitalDetailTemplate.TabNavigation />
          <HospitalDetailTemplate.Reviews />
          <HospitalDetailTemplate.Introduction />
          <HospitalDetailTemplate.Doctors />
          <HospitalDetailTemplate.OfficeHours />
          <HospitalDetailTemplate.PaymentOptions />
          <HospitalDetailTemplate.Access />
          <HospitalDetailTemplate.Events />
          <HospitalDetailTemplate.CTAButtons />
        </HospitalDetailTemplate>
      </TopDownloadScreen>

      <StructuredData
        schemas={[
          // 1. 병원 정보 (LocalBusiness + MedicalBusiness)
          createHospitalSchema(hospital, locale, {
            doctors,
            services: events,
          }),

          // 2. 의사 정보 (각 의사별 Physician 스키마)
          ...doctors.map((doctor) => createDoctorSchema(doctor, hospital, locale)),

          // 3. 리뷰 정보 (상위 5개 리뷰만)
          ...((reviewWithPagination?.contents || []).slice(0, 5).map((review) =>
            createReviewSchema(
              {
                id: review.id ? `review-${review.id}` : undefined,
                reviewBody: review.translation?.description || review.description || '',
                totalRating: review.totalRating || undefined,
                author: {
                  nickName: review.author.nickName || '',
                },
              },
              hospital,
              locale,
            ),
          ) || []),
          // 4. 조직 정보 (강남언니)
          createOrganizationSchema(locale),
        ]}
      />
    </ResponsiveLayout>
  )
}

export const getServerSideProps = async (
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<HospitalDetailPageProps>> => {
  const contextEnhancer = new ContextEnhancer(ctx)
  const enhancedContext = (await contextEnhancer.activateAuth()).activateLocale().getContext()
  const { t } = getServerTranslation(enhancedContext.locale)
  const hospitalId = Number.parseInt((enhancedContext.params?.hospitalId as string) || '', 10)
  if (!hospitalId || Number.isNaN(hospitalId)) {
    enhancedContext.res.setHeader('x-robots-tag', 'noindex, nofollow')
    return {
      redirect: {
        destination: `${enhancedContext.pathnamePrefix}/404`,
        permanent: false,
      },
    }
  }

  const hospitalService = new HospitalService(createSolarApiClient({ headers: enhancedContext.apiHeaders }))
  const doctorService = new DoctorService(createSolarApiClient({ headers: enhancedContext.apiHeaders }))

  try {
    const [hospitalResponse, doctorsResponse, eventsResponse, reviewsResponse] = await Promise.allSettled([
      hospitalService.getHospital(hospitalId),
      doctorService.getDoctorsInHospital(hospitalId),
      getEventsInHospital(hospitalId, enhancedContext.apiHeaders),
      enhancedContext.isAnonymousUser && enhancedContext.country !== Countries.JP
        ? Promise.resolve<ReviewsPaginationResponseInternal>({
            contents: [],
            totalPageNumber: 0,
            totalElementNumber: 0,
            currentPageIndex: 0,
            pageSize: 0,
            isLastPage: true,
          })
        : getHospitalReviewsInternal(enhancedContext.apiHeaders, hospitalId),
    ])

    if (hospitalResponse.status === 'rejected') {
      const errorMessage = hospitalResponse?.reason?.response?.data?.error?.message
      if (errorMessage === '해당 병원은 유저 국가에 지원되지 않는 병원입니다.') {
        return {
          redirect: {
            destination: `${enhancedContext.pathnamePrefix}/hospitals`,
            permanent: false,
          },
        }
      }
      return {
        redirect: {
          destination: `${enhancedContext.pathnamePrefix}/hospitals`,
          permanent: false,
        },
      }
    }

    const hospital = hospitalResponse.value
    const doctors = doctorsResponse.status === 'fulfilled' ? doctorsResponse.value : []
    const events = eventsResponse.status === 'fulfilled' ? eventsResponse.value.contents : []
    const reviewWithPagination =
      reviewsResponse.status === 'fulfilled'
        ? reviewsResponse.value
        : {
            contents: [],
            totalPageNumber: 0,
            totalElementNumber: 0,
            currentPageIndex: 0,
            pageSize: 0,
            isLastPage: true,
          }

    const data = {
      hospital,
      doctors,
      events,
      reviewWithPagination,
    }

    return {
      props: {
        user: enhancedContext.user,
        data,
        meta: getDefaultMeta(enhancedContext.locale, {
          ...getMeta(t, hospital, events, enhancedContext.locale),
          canonical: `${getEnv('NEXT_PUBLIC_HOST')}${enhancedContext.pathnamePrefix}/hospitals/${hospital.hospitalId}`,
        }),
      },
    }
  } catch (e) {
    console.error(e)
    return {
      redirect: {
        destination: `${enhancedContext.pathnamePrefix}/hospitals`,
        permanent: false,
      },
    }
  }
}

const getMeta = (
  t: (key: string) => string,
  hospital: HospitalView,
  events: ServiceOffer[],
  locale: Locales,
): SEOMeta => {
  const ogTagsText = sortEventOpTags(events)
    .slice(0, 4)
    .join(locale === Locales.JA_JP ? ',' : ', ')
  const hospitalName = hospital?.name
  const subwayNames = hospital?.location?.subwayNames?.join(locale === Locales.JA_JP ? '' : ', ')
  const hospitalSido = hospital?.location?.sido
  const reviewCount = hospital?.statistic?.reviewCount

  const meta = {
    [Locales.JA_JP]: {
      title: `${hospitalName}の口コミ・料金 | カンナムオンニ`,
      description: `${hospitalSido ? `${hospitalSido} ` : ''}${subwayNames ? `${subwayNames}` : ''}${hospitalName}. ${getOgDescriptionByLocale(locale, reviewCount, ogTagsText)}`,
      ogDescription: getOgDescriptionByLocale(locale, reviewCount, ogTagsText),
    },
    [Locales.KO_KR]: {
      title: `${hospitalName} | ${t('common.meta.title')}`,
      description: `${hospitalSido ? `${hospitalSido} ` : ''}${subwayNames ? `${subwayNames}` : ''}${hospitalName}. ${getOgDescriptionByLocale(locale, reviewCount, ogTagsText)}`,
      ogDescription: getOgDescriptionByLocale(locale, reviewCount, ogTagsText),
    },
    [Locales.EN_US]: {
      title: `${hospitalName} | ${t('common.meta.title')}`,
      description: `${hospitalSido ? `${hospitalSido} ` : ''}${subwayNames ? `${subwayNames}` : ''}${hospitalName}. ${getOgDescriptionByLocale(locale, reviewCount, ogTagsText)}`,
      ogDescription: getOgDescriptionByLocale(locale, reviewCount, ogTagsText),
    },
    [Locales.TH_TH]: {
      title: `ดูรีวิวจริง และโปรโมชั่นดีๆ จาก ${hospitalName} | UNNI - แอปเกาหลีอันดับ 1`,
      description: `${hospitalName}, ${subwayNames || hospitalSido} มีรีวิวทั้งหมด ${reviewCount} รายการ ดูรีวิวและโปรโมชั่นดีๆ เลยในแอป UNNI`,
      ogDescription: getOgDescriptionByLocale(locale, reviewCount, ogTagsText),
    },
    [Locales.ZH_HANS_CN]: {
      title: `${hospitalName} | ${t('common.meta.title')}`,
      description: `${hospitalSido ? `${hospitalSido} ` : ''}${subwayNames ? `${subwayNames}` : ''}${hospitalName}. ${getOgDescriptionByLocale(locale, reviewCount, ogTagsText)}`,
      ogDescription: getOgDescriptionByLocale(locale, reviewCount, ogTagsText),
    },
    [Locales.ZH_HANT_TW]: {
      title: `${hospitalName} | ${t('common.meta.title')}`,
      description: `${hospitalSido ? `${hospitalSido} ` : ''}${subwayNames ? `${subwayNames}` : ''}${hospitalName}. ${getOgDescriptionByLocale(locale, reviewCount, ogTagsText)}`,
      ogDescription: getOgDescriptionByLocale(locale, reviewCount, ogTagsText),
    },
  }

  return {
    twtCardType: t('common.meta.twtCardType'),
    type: t('common.meta.type'),
    ...meta[locale],
  }
}

const getOgDescriptionByLocale = (locale: Locales, reviewCount: number, ogTagsText: string) => {
  const ogDescriptions = {
    [Locales.EN_US]: `${reviewCount > 0 ? `${reviewCount} reviews are available.` : ''}${ogTagsText ? ` Check prices for ${ogTagsText} and more.` : '1st plastic surgery & skin care information app in Korea'}`,
    [Locales.TH_TH]: `${reviewCount > 0 ? `${reviewCount} รีวิวมีอยู่` : ''}${ogTagsText ? ` ตรวจสอบราคาของ ${ogTagsText} เป็นต้นไป` : 'แอปการรักษาสวยที่มีความนิยมที่สุดในประเทศไทย'}`,
    [Locales.KO_KR]: `${reviewCount > 0 ? `${reviewCount}개의 리뷰가 있습니다.` : ''}${ogTagsText ? ` ${ogTagsText} 등의 가격을 확인해보세요.` : '1등 성형수술&피부시술 정보앱 강남언니'}`,
    [Locales.JA_JP]: `${reviewCount > 0 ? `${reviewCount}件の口コミがあります。` : ''}${ogTagsText ? `${ogTagsText}などの値段を確認してみてください。` : '日本最大級の美容医療・整形の口コミ予約アプリ。'}`,
    [Locales.ZH_HANS_CN]: `${reviewCount > 0 ? `${reviewCount}条评论` : ''}${ogTagsText ? ` 查看 ${ogTagsText} 等的价格` : '韩国第一整形&皮肤科信息应用'}`,
    [Locales.ZH_HANT_TW]: `${reviewCount > 0 ? `${reviewCount}條評論` : ''}${ogTagsText ? ` 查看 ${ogTagsText} 等的價格` : '韓國第一整形&皮膚科信息應用'}`,
  }
  return ogDescriptions[locale]
}

/**
 * 정책: tag가 포함된 event의 rating count 합이 높은 순으로 정렬
 * 합이 동일한 경우, tag가 포함된 event의 개수가 많은 순으로 정렬
 */
const sortEventOpTags = (events: ServiceOffer[]): string[] => {
  try {
    const tagCountAndRatingCountSumMap = new Map<string, [tagCount: number, ratingCountSum: number]>()
    for (const event of events) {
      for (const opTag of event?.badges || []) {
        tagCountAndRatingCountSumMap.set(opTag, [
          (tagCountAndRatingCountSumMap.get(opTag)?.[0] ?? 0) + 1,
          (tagCountAndRatingCountSumMap.get(opTag)?.[1] ?? 0) + event.rating.ratingCount,
        ])
      }
    }
    const sortedOpTags = Array.from(tagCountAndRatingCountSumMap.entries())
      .sort(([, [aTagCount, aRatingCountSum]], [, [bTagCount, bRatingCountSum]]) => {
        if (aRatingCountSum === bRatingCountSum) {
          return bTagCount - aTagCount
        }
        return bRatingCountSum - aRatingCountSum
      })
      .map(([opTag]) => opTag)
    return sortedOpTags
  } catch (e) {
    console.error(e)
    return []
  }
}

const getEventsInHospital = async (hospitalId: number, apiHeaders: ApiHeaders) => {
  const serviceOfferService = new ServiceOfferService(createSolarApiClient({ headers: apiHeaders }))

  return serviceOfferService.getServiceOfferListWithFilters({
    filters: {
      hospital: { id: hospitalId },
    },
    pagination: {
      pageSize: 500,
      pageIndex: 0,
    },
  })
}

export default HospitalDetailPage
